import React, {
  useState, useEffect, useRef, useMemo, useCallback
} from 'react';
import { hierarchy, treemap, treemapSquarify } from 'd3-hierarchy';
import { SP500 } from '../lib/sp500';
import {
  getMarketColor, getTransactionColor, buildTransactionGradient,
  parseAmount, normalizeAmount, fmtPct, fmtMktCap, getTileFontSize
} from '../lib/colors';
import HoverCard from './HoverCard';

async function fetchJsonWithFallback(urls) {
  let lastError = null;
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status}`);
        continue;
      }
      return await res.json();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Request failed');
}

// Allowed metrics for market view
export const METRIC_OPTIONS = [
  { value: '1d',  label: '1D' },
  { value: '1w',  label: '1W' },
  { value: '1m',  label: '1M' },
  { value: '3m',  label: '3M' },
  { value: '1y',  label: '1Y' },
];

const LOGO_BASE = 'https://assets.parqet.com/logos/symbol/';

function useSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

// Build grouped + sorted d3 hierarchy from market data
function buildMarketHierarchy(sp500, quotes, sectorFilter, subindustryFilter = 'all', metricChanges = null) {
  // Support comma-separated multi-sector filter, e.g. 'Utilities,Financials'
  const sectorSet =
    sectorFilter && sectorFilter !== 'all' && sectorFilter.trim()
      ? new Set(sectorFilter.split(',').map(s => s.trim()).filter(Boolean))
      : null;
  const subSet =
    subindustryFilter && subindustryFilter !== 'all' && subindustryFilter.trim()
      ? new Set(subindustryFilter.split(',').map(s => s.trim()).filter(Boolean))
      : null;

  const sectors = {};
  const addedTickers = new Set(); // prevent duplicate tiles

  sp500.forEach(stock => {
    if (sectorSet && !sectorSet.has(stock.sector)) return;
    if (subSet && !subSet.has(stock.sub)) return;
    const q = quotes[stock.ticker];
    if (!q) return;
    addedTickers.add(stock.ticker);
    const size = q.marketCap && q.marketCap > 0 ? q.marketCap : 1;
    if (!sectors[stock.sector]) {
      sectors[stock.sector] = { name: stock.sector, children: [] };
    }
    sectors[stock.sector].children.push({
      ticker: stock.ticker,
      name: stock.name,
      sector: stock.sector,
      sub: stock.sub,
      marketCap: size,
      changePercent: metricChanges?.[stock.ticker] ?? q.changePercent,
      price: q.price,
      change: q.change,
    });
  });

  // Add any tickers present in quotes but NOT already in the sp500 array above.
  // (In market mode this catches quotes outside the static SP500 list;
  //  in transactions mode the sp500 array already contains every ticker, so
  //  addedTickers.has() will skip them all, preventing duplicates.)
  Object.keys(quotes).forEach(ticker => {
    if (addedTickers.has(ticker)) return;
    const q = quotes[ticker];
    if (!q) return;
    const size = q.marketCap && q.marketCap > 0 ? q.marketCap : 1;
    const sector = 'Other';
    const sub = 'NA';
    if (sectorSet && !sectorSet.has(sector)) return;
    if (subSet && !subSet.has(sub)) return;
    if (!sectors[sector]) sectors[sector] = { name: sector, children: [] };
    sectors[sector].children.push({
      ticker,
      name: q.shortName || ticker,
      sector,
      sub,
      marketCap: size,
      changePercent: metricChanges?.[ticker] ?? q.changePercent,
      price: q.price,
      change: q.change,
    });
  });

  return { name: 'root', children: Object.values(sectors) };
}

// Calculate treemap layout
function calcTreemap(data, width, height) {
  if (!data || !data.children?.length || width < 4 || height < 4) return null;
  const root = hierarchy(data)
    .sum(d => d.marketCap || 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));
  const layout = treemap()
    .tile(treemapSquarify)
    .size([width, height])
    .paddingOuter(4)
    .paddingTop(18)
    .paddingInner(1)
    .round(true);
  return layout(root);
}

// ─────────────────────────────────────────────────────────────────────────────
// StockTile – single stock block
// ─────────────────────────────────────────────────────────────────────────────
function StockTile({ leaf, mode, transactions, metric, onMouseEnter, onMouseLeave, onClick }) {
  const w = leaf.x1 - leaf.x0;
  const h = leaf.y1 - leaf.y0;
  const { ticker, changePercent } = leaf.data;

  // Determine background
  let bg;
  if (mode === 'market') {
    const pct = metric === '1d'
      ? changePercent
      : changePercent; // For other periods, same data unless fetched separately
    bg = getMarketColor(pct);
  } else {
    // Transaction mode: build stripes
    const txs = (transactions || [])
      .filter(t => t.ticker === ticker)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)); // newest first (top)

    if (txs.length === 0) {
      bg = '#363A45';
    } else {
      const allAmts = (transactions || []).map(t => parseAmount(t.amount));
      const minAmt = Math.min(...allAmts.filter(a => a > 0));
      const maxAmt = Math.max(...allAmts);

      if (txs.length === 1) {
        const intensity = normalizeAmount(parseAmount(txs[0].amount), minAmt, maxAmt);
        bg = getTransactionColor(txs[0].type, intensity);
      } else {
        const stripes = txs.map(tx => {
          const amt = parseAmount(tx.amount);
          return {
            type: tx.type,
            intensity: normalizeAmount(amt, minAmt, maxAmt),
            weight: Math.max(amt, 1),
          };
        });
        bg = buildTransactionGradient(stripes);
      }
    }
  }

  const fs = getTileFontSize(w, h);
  const showLogo = mode === 'market' && fs >= 9 && w >= 46 && h >= 44;
  const logoSize = Math.min(Math.floor(Math.min(w, h) * 0.28), 28);
  const showName = fs >= 6 && w >= 18 && h >= 14;
  const showPct = mode === 'market' && fs >= 8 && h >= 32 && w >= 26;

  const pctDisplay = mode === 'market'
    ? fmtPct(changePercent)
    : null;

  return (
    <div
      className="stonks-heatmap-tile"
      style={{
        left: leaf.x0,
        top: leaf.y0,
        width: w,
        height: h,
        background: bg,
      }}
      onMouseEnter={onMouseEnter ? (e => onMouseEnter(ticker, e)) : undefined}
      onMouseLeave={onMouseLeave || undefined}
      onClick={onClick ? (e => onClick(ticker, e)) : undefined}
    >
      {showName && (
        <div className="stonks-tile-content">
          {showLogo && (
            <LogoImg
              ticker={ticker}
              size={logoSize}
            />
          )}
          <span
            className="stonks-tile-ticker"
            style={{ fontSize: Math.max(6, fs) }}
          >
            {ticker}
          </span>
          {showPct && pctDisplay && (
            <span
              className="stonks-tile-pct"
              style={{ fontSize: Math.max(7, fs - 1) }}
            >
              {pctDisplay}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function LogoImg({ ticker, size }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className="stonks-tile-logo-fallback"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
      >
        {ticker.charAt(0)}
      </div>
    );
  }
  return (
    <img
      className="stonks-tile-logo"
      src={`${LOGO_BASE}${ticker}`}
      alt=""
      width={size}
      height={size}
      onError={() => setFailed(true)}
      style={{ width: size, height: size }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main StockHeatmap component
// ─────────────────────────────────────────────────────────────────────────────
export default function StockHeatmap({
  mode = 'market',      // 'market' | 'transactions'
  tickers,              // for transactions mode: array of ticker strings
  transactions,         // for transactions mode: array of { ticker, type, date, amount }
  metric = '1d',
  sectorFilter = 'all',
  subindustryFilter = 'all',
}) {
  const containerRef = useRef(null);
  const { width, height } = useSize(containerRef);

  const [quotes, setQuotes] = useState({});
  const [metricChanges, setMetricChanges] = useState({});
  const [metricLoading, setMetricLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hover state
  const [hoverTicker, setHoverTicker] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [hoverLoading, setHoverLoading] = useState(false);
  const hoverFetchRef = useRef(null);
  const lastHoverRequestRef = useRef('');

  const closeCard = useCallback(() => {
    setHoverTicker(null);
    setHoverInfo(null);
    setHoverLoading(false);
    if (hoverFetchRef.current) clearTimeout(hoverFetchRef.current);
  }, []);

  const fetchTickerDetails = useCallback(async (ticker) => {
    if (!ticker) return;
    lastHoverRequestRef.current = ticker;
    setHoverLoading(true);
    try {
      const data = await fetchJsonWithFallback([
        `/api/stonksQuote?ticker=${ticker}`,
        `/.netlify/functions/stonksQuote?ticker=${ticker}`,
      ]);
      if (lastHoverRequestRef.current === ticker && data.info) {
        setHoverInfo(data.info);
      }
    } catch (_) {
      // no-op
    } finally {
      if (lastHoverRequestRef.current === ticker) {
        setHoverLoading(false);
      }
    }
  }, []);

  // Fetch market data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const tickerParam = tickers?.length
      ? tickers.join(',')
      : ''; // empty = server uses default SP500 list

    const includeProfile = '&includeProfile=1';

    const urls = tickerParam
      ? [
          `/api/stonksMarket?tickers=${encodeURIComponent(tickerParam)}${includeProfile}`,
          `/.netlify/functions/stonksMarket?tickers=${encodeURIComponent(tickerParam)}${includeProfile}`,
        ]
      : [
          '/api/stonksMarket',
          '/.netlify/functions/stonksMarket',
        ];

    fetchJsonWithFallback(urls)
      .then(data => {
        if (cancelled) return;
        if (data.quotes && Object.keys(data.quotes).length) {
          setQuotes(data.quotes);
        } else {
          setError('No market data returned');
        }
      })
      .catch(e => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tickers?.join(','), mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch actual multi-period returns for Market View metrics beyond 1D.
  useEffect(() => {
    if (mode !== 'market') return;
    if (metric === '1d') {
      setMetricChanges({});
      setMetricLoading(false);
      return;
    }

    const sectorSet = sectorFilter && sectorFilter !== 'all' && sectorFilter.trim()
      ? new Set(sectorFilter.split(',').map(s => s.trim()).filter(Boolean))
      : null;
    const subSet = subindustryFilter && subindustryFilter !== 'all' && subindustryFilter.trim()
      ? new Set(subindustryFilter.split(',').map(s => s.trim()).filter(Boolean))
      : null;

    const metricTickers = SP500
      .map(s => ({
        ticker: s.ticker,
        sector: quotes[s.ticker]?.sector || 'Other',
        sub: quotes[s.ticker]?.industry || 'NA',
      }))
      .filter(s => (!sectorSet || sectorSet.has(s.sector)) && (!subSet || subSet.has(s.sub)))
      .map(s => s.ticker);

    if (!metricTickers.length) {
      setMetricChanges({});
      setMetricLoading(false);
      return;
    }

    let cancelled = false;
    setMetricLoading(true);
    fetchJsonWithFallback([
      `/api/stonksPerformance?metric=${encodeURIComponent(metric)}&tickers=${encodeURIComponent(metricTickers.join(','))}`,
      `/.netlify/functions/stonksPerformance?metric=${encodeURIComponent(metric)}&tickers=${encodeURIComponent(metricTickers.join(','))}`,
    ])
      .then(data => {
        if (!cancelled) setMetricChanges(data?.changes || {});
      })
      .catch(() => {
        if (!cancelled) setMetricChanges({});
      })
      .finally(() => {
        if (!cancelled) setMetricLoading(false);
      });

    return () => { cancelled = true; };
  }, [metric, mode, sectorFilter, subindustryFilter, quotes]);

  // Build hierarchy
  const hierarchyData = useMemo(() => {
    if (!Object.keys(quotes).length) return null;
    const sp500 = mode === 'market'
      ? SP500.map(s => {
          const q = quotes[s.ticker] || {};
          return {
            ticker: s.ticker,
            name: q.shortName || s.name,
            sector: q.sector || 'Other',
            sub: q.industry || 'NA',
          };
        })
      : (tickers || []).map(t => {
      const q = quotes[t] || {};
      return {
        ticker: t,
        name: q.shortName || t,
        sector: q.sector || 'Other',
        sub: q.industry || 'NA',
      };
    });
    return buildMarketHierarchy(sp500, quotes, sectorFilter, subindustryFilter, mode === 'market' ? metricChanges : null);
  }, [quotes, mode, tickers, sectorFilter, subindustryFilter, metricChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  // Treemap layout
  const tree = useMemo(
    () => calcTreemap(hierarchyData, width, height),
    [hierarchyData, width, height]
  );

  // Hover handlers
  const handleMouseEnter = useCallback((ticker, e) => {
    if (mode === 'transactions') return;
    setHoverTicker(ticker);
    setHoverPos({ x: e.clientX, y: e.clientY });
    setHoverInfo(null);

    if (hoverFetchRef.current) clearTimeout(hoverFetchRef.current);
    hoverFetchRef.current = setTimeout(() => {
      fetchTickerDetails(ticker);
    }, 220);
  }, [fetchTickerDetails, mode]);

  const handleTileClick = useCallback((ticker, e) => {
    if (mode !== 'transactions') return;
    e.stopPropagation();
    if (hoverFetchRef.current) clearTimeout(hoverFetchRef.current);
    setHoverTicker(ticker);
    setHoverInfo(null);
    setHoverPos({ x: e.clientX, y: e.clientY });
    fetchTickerDetails(ticker);
  }, [fetchTickerDetails, mode]);

  const handleMouseMove = useCallback((e) => {
    if (mode === 'transactions') return;
    if (hoverTicker) {
      setHoverPos({ x: e.clientX, y: e.clientY });
    }
  }, [hoverTicker, mode]);

  const handleMouseLeave = useCallback(() => {
    if (mode === 'transactions') return;
    closeCard();
  }, [closeCard, mode]);

  // Always render the outer container so containerRef is mounted and
  // ResizeObserver can fire. Without this, width/height stay 0 while loading,
  // and the treemap can never be computed after the fetch resolves.
  const leaves = tree ? tree.leaves() : [];
  const sectorNodes = tree ? (tree.children || []) : [];
  const hoverTransactions = hoverTicker
    ? (transactions || [])
        .filter(t => t.ticker === hoverTicker)
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    : [];

  return (
    <div className="stonks-heatmap-wrap" ref={containerRef}>

      {/* Loading overlay */}
      {loading && (
        <div className="stonks-loading">
          <div className="stonks-spinner" />
          <span>Loading market data…</span>
        </div>
      )}

      {/* Error overlay */}
      {!loading && error && (
        <div className="stonks-empty">
          <div className="stonks-empty-icon">⚠️</div>
          <div style={{ color: 'var(--s-red)' }}>Error: {error}</div>
        </div>
      )}

      {/* Empty overlay */}
      {!loading && !error && !tree && (
        <div className="stonks-empty">
          <div className="stonks-empty-icon">📊</div>
          <div>No data to display</div>
        </div>
      )}

      {/* Return-metric loading overlay */}
      {!loading && metricLoading && (
        <div className="stonks-heatmap-metric-loading">
          <div className="stonks-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
          <span>Updating returns…</span>
        </div>
      )}

      {/* Heatmap tiles – only shown once dimensions + data are ready */}
      {tree && (
        <div
          className="stonks-heatmap-inner"
          onMouseMove={handleMouseMove}
        >
          {/* Sector label overlays */}
          {sectorNodes.map(sectorNode => {
            const sw = sectorNode.x1 - sectorNode.x0;
            if (sw < 40) return null;
            return (
              <div
                key={sectorNode.data.name}
                className="stonks-sector-label"
                style={{
                  left: sectorNode.x0 + 4,
                  top: sectorNode.y0 + 1,
                  maxWidth: sw - 8,
                }}
              >
                {sectorNode.data.name}
              </div>
            );
          })}

          {/* Stock tiles */}
          {leaves.map(leaf => (
            <StockTile
              key={leaf.data.ticker}
              leaf={leaf}
              mode={mode}
              transactions={transactions}
              metric={metric}
              onMouseEnter={mode === 'market' ? handleMouseEnter : null}
              onMouseLeave={mode === 'market' ? handleMouseLeave : null}
              onClick={mode === 'transactions' ? handleTileClick : null}
            />
          ))}
        </div>
      )}

      {mode === 'transactions' && hoverTicker && (
        <div className="stonks-hovercard-backdrop" onClick={closeCard} />
      )}

      {/* Hover card */}
      <HoverCard
        ticker={hoverTicker}
        quickQuote={hoverTicker ? quotes[hoverTicker] : null}
        info={hoverInfo}
        loading={hoverLoading}
        pos={hoverPos}
        visible={!!hoverTicker}
        interactive={mode === 'transactions'}
        onClose={closeCard}
        taxonomyInfo={hoverTicker ? ({
          sector: quotes[hoverTicker]?.sector || 'Other',
          subIndustry: quotes[hoverTicker]?.industry || 'NA',
        }) : null}
        transactions={hoverTransactions}
      />
    </div>
  );
}
